import browser from 'Cloud/browser';
import config from 'Cloud/config';
import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_ONPREMISE, IS_TABLET_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { abSharingLinkFakedoor } from 'reactApp/appHelpers/featuresHelpers';
import { disablePublicDownload } from 'reactApp/appHelpers/featuresHelpers/features/disablePublicDownload';
import { enabledPublicDownloadLimits, tooltipLimitSharing } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { isPublicPasswordEnabled } from 'reactApp/appHelpers/featuresHelpers/features/publicPassword';
import { seoEnable } from 'reactApp/appHelpers/featuresHelpers/features/seoEnable';
import { showWeblinkListFileSharing } from 'reactApp/appHelpers/featuresHelpers/features/tabsFolderSharingDesign';
import { isThemedSharedModalItem } from 'reactApp/appHelpers/featuresHelpers/features/themedPublic';
import { unPublishHelper } from 'reactApp/appHelpers/publishHelper';
import { PaidFeatureBlock, SharingWidnowContext } from 'reactApp/components/SharingWindow/PaidFeaturesBlock/PaidFeatureBlock';
import { sendGa } from 'reactApp/components/SharingWindow/Sharing.helpers';
import { EFrom, EPromoTooltip } from 'reactApp/components/SharingWindow/Sharing.types';
import { useOnboardingEnabled } from 'reactApp/components/SharingWindow/SharingNew/hooks/onboardingEnabled.hook';
import { useWeblinkOpened } from 'reactApp/components/SharingWindow/SharingNew/hooks/useWeblinkOpened.hook';
import { SharingNewSection } from 'reactApp/components/SharingWindow/SharingNew/Section/SharingNewSection';
import { AutoDelete } from 'reactApp/components/SharingWindow/SharingNew/Weblink/AutoDelete/AutoDelete';
import { DISABLE_PUBLIC_DOWNLOAD_TOOLTIP_ID } from 'reactApp/components/SharingWindow/SharingNew/Weblink/DisablePublicDownload/Tooltip/Tooltip';
import { useSeoOption } from 'reactApp/components/SharingWindow/SharingNew/Weblink/hooks/useSeoOption';
import { PUBLIC_PASSWORD_TOOLTIP_ID } from 'reactApp/components/SharingWindow/SharingNew/Weblink/PublicPassword/Tooltip/Tooltip';
import SharingOptionAccessRights from 'reactApp/components/SharingWindow/SharingOptions/SharingOptionAccessRights';
import { SharingOptionDomain } from 'reactApp/components/SharingWindow/SharingOptions/SharingOptionDomain';
import { SharingOptionExpires } from 'reactApp/components/SharingWindow/SharingOptions/SharingOptionExpires';
import styles from 'reactApp/components/SharingWindow/Weblink/SharingWeblink.css';
import { useIsWindowIntegrationSmall } from 'reactApp/hooks/useIsWindowIntegrationSmall';
import { getEditorsFor, getIsFileForCoEditing } from 'reactApp/modules/editor/editor.selectors';
import {
    getFeatureEditingNoneditableFormats,
    getFeaturePublicAutoDeletePromo,
    getFeaturePublicDomestic,
} from 'reactApp/modules/features/features.selectors';
import { getIntegrationClient } from 'reactApp/modules/integration/integration.selectors';
import { publishRequest } from 'reactApp/modules/modifying/modifying.actions';
import { loadPaidInfoRequest } from 'reactApp/modules/paidInfo/paidInfo.module';
import { getPublicSharingInfo } from 'reactApp/modules/public/public.actions';
import { getPublicLimits, getPublicPin } from 'reactApp/modules/public/public.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { isAutoDeleteEnabled } from 'reactApp/modules/sharedAutoDelete/sharedAutoDelete.selectors';
import { getItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserStorageSelectors } from 'reactApp/modules/user/userStorage';
import type { RootState } from 'reactApp/store';
import { CopyWeblink } from 'reactApp/ui/CopyWeblink/CopyWeblink';
import { MAX_SHOWN_COUNT, TOOLTIP_ID } from 'reactApp/ui/SharingTooltips/SharingTooltipPromo';
import { getPublicUrl } from 'reactApp/utils/urlHelper';

import { renderUnpublishWeblinkDialog } from '../../Weblink/SharingWeblink.helpers';
import type { IProps } from '../../Weblink/SharingWeblink.types';
import { InformerLimitSharing } from '../InformerLimitSharing/InformerLimitSharing';
import { ChangeDesign, PUBLIC_DESIGN_TOOLTIP_ID } from './ChangeDesign/ChangeDesign';
import { DisablePublicDownload } from './DisablePublicDownload/DisablePublicDownload';
import { PublicPassword } from './PublicPassword/PublicPassword';
import { SharingNewWeblinkList } from './WeblinkList/SharingNewWeblinkList';

const IS_FREE_B2B_BIZ_EDITORS_USER = config.get('FREE_B2B_BIZ_EDITORS_USER');

let lastShownTooltip;

/* eslint-disable complexity */
// eslint-disable-next-line max-lines-per-function
export const SharingNewWeblink = memo(function SharingNewWeblink(props: IProps): ReactElement | null {
    const {
        id,
        isOwn,
        item,
        from,
        isSharedOrMouted,
        gaSuffix,
        noEdit = false,
        isPublic,
        isPhone,
        onCloseModal,
        onUnpublish,
        sendSharingContentAnalytics,
        withPromoTooltip,
        showListSharing,
        folderName,
        onboardingEnabled,
        formProps,
    } = props;
    const dispatch = useDispatch();
    const itemStorage = useSelector(getCurrentStorage);
    const storage = item?.storage || itemStorage;
    const isAlbum = from === EFrom.ALBUMS || storage === EStorageType.albums;
    const isVigbo = storage === EStorageType.vigbo;
    const isWindowSmall = useIsWindowIntegrationSmall();
    const { isTutoria } = useSelector(getIntegrationClient);
    const isBizUser = useSelector(UserSelectors.isBizUser);
    const isCorpUser = useSelector(UserSelectors.isCorpUser);
    const isPublished = Boolean(item?.weblink);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    // Исключаем стоки, у которых них нет публичной ссылки
    const publicUrl = (isPublished && getPublicUrl(item, isAlbum || isVigbo)) || '';
    const disableDownload = item?.weblinkEnabledDisabledDownload;

    const isStock = itemStorage === EStorageType.stock;
    const { isWeblinkOpened, setWeblinkOpened } = useWeblinkOpened(from, isPublished);

    const seoOption = useSeoOption(item, !isBizUser && !IS_ONPREMISE && !isSharedOrMouted.isMounted && seoEnable);

    const showDisablePublicDownload =
        isWeblinkOpened && !isBizUser && !IS_ONPREMISE && !isSharedOrMouted.isMounted && disablePublicDownload;

    const isDomesticFeatureEnabled = useSelector(getFeaturePublicDomestic);
    const canShareForDomestic = isBizUser || isCorpUser;
    const showDomesticSettings =
        (!isAlbum || !isVigbo || !isStock) &&
        ((canShareForDomestic && Boolean(isDomesticFeatureEnabled)) || (showWeblinkListFileSharing && !isAlbum && !isVigbo)) &&
        isOwn;

    const editingNoneEditableFormatsFeature = useSelector((state: RootState) => {
        if (!item) {
            return;
        }
        return getFeatureEditingNoneditableFormats(state, {
            file: {
                ext: typeof item === 'object' && 'ext' in item ? item.ext : undefined,
                size: typeof item === 'object' && 'size' in item ? item.size : undefined,
            },
            storage,
        });
    });
    const convertExt = editingNoneEditableFormatsFeature?.ext || '';
    const isFolder = item?.isFolder;

    const isConvertFileForCoEditing = useSelector((state: RootState) => getIsFileForCoEditing(state, { ...item, ext: convertExt }));
    const isFileForCoEditing = useSelector((state: RootState) => getIsFileForCoEditing(state, item));
    const isEditable = !!useSelector((state: RootState) => getEditorsFor(state, convertExt ? { ...item, ext: convertExt } : item))?.length;

    const isDocumentForEdit =
        (item?.kind === 'document' && IS_FREE_B2B_BIZ_EDITORS_USER) ||
        isFileForCoEditing ||
        (isConvertFileForCoEditing && !(IS_TABLET_BROWSER || browser.isIpadOs()) && isEditable);
    const isReadOnlyItem = !!item?.isReadOnly;
    const parent = useSelector((state: RootState) => getItemById(state, item?.parent || ''));

    const isReadOnlyParent = !!(parent && 'isReadOnly' in parent && parent?.isReadOnly);
    const isReadOnlyProperty = !isReadOnlyItem && isSharedOrMouted.isMounted ? isReadOnlyParent : isReadOnlyItem;
    const isReadOnly = isFolder ? isReadOnlyProperty : isReadOnlyProperty || !isDocumentForEdit;
    const { onboardingAllUsers, onboardingPaidUsers } = useOnboardingEnabled(onboardingEnabled);
    // tempexp_17847-start
    const downloadLimits = useSelector((state: RootState) => getPublicLimits(state, item?.weblink || ''));
    const showInformerLimitSharing = !onboardingAllUsers && enabledPublicDownloadLimits && !isPaidUser && downloadLimits?.left === 0;
    const showSharingTooltipPromo =
        !onboardingAllUsers && enabledPublicDownloadLimits && tooltipLimitSharing && !isPaidUser && isWeblinkOpened && !!downloadLimits;
    // tempexp_17847-end

    const isAutodeleteFeatureEnabled = Boolean(useSelector(isAutoDeleteEnabled));
    const isAutodeletePromoFeatureEnabled = Boolean(useSelector(getFeaturePublicAutoDeletePromo));
    const showExpires = isStock || (!isAlbum && !isVigbo && !isPublic && isOwn);
    const showAccessRights = !isAlbum && !isVigbo && isOwn;
    // Отключаем платную фичу в Тутории
    const showAutoDeleteSettings =
        !onboardingAllUsers &&
        !isTutoria &&
        isOwn &&
        !isAlbum &&
        !isVigbo &&
        item?.isFolder &&
        !isSharedOrMouted.isMounted &&
        (isAutodeleteFeatureEnabled || isAutodeletePromoFeatureEnabled);
    // tempexp_17341-next-line
    const showChangeDesign =
        !onboardingAllUsers &&
        isWeblinkOpened &&
        isThemedSharedModalItem &&
        !isAlbum &&
        !isVigbo &&
        isOwn &&
        item?.isFolder &&
        !isTutoria &&
        !isPhone;
    // tempexp_17605-start
    const pin = useSelector((state: RootState) => getPublicPin(state, item?.weblink || ''));
    const showChangePassword =
        !onboardingAllUsers &&
        isWeblinkOpened &&
        (isPublicPasswordEnabled || Boolean(pin)) &&
        !isAlbum &&
        !isVigbo &&
        isOwn &&
        item?.isFolder &&
        !isTutoria;
    // tempexp_17605-end

    const tooltipDownloadLimits = useSelector((state) => UserStorageSelectors.get(state, TOOLTIP_ID));
    const showDownloadLimitsTip =
        showSharingTooltipPromo &&
        (!lastShownTooltip || lastShownTooltip === EPromoTooltip.SharingDownloadLimits) &&
        (withPromoTooltip === EPromoTooltip.SharingDownloadLimits || !(tooltipDownloadLimits > MAX_SHOWN_COUNT));

    const tooltipPasswordShown = useSelector((state) => UserStorageSelectors.get(state, PUBLIC_PASSWORD_TOOLTIP_ID));
    const defaultShowPublicPasswordTip =
        showChangePassword &&
        (!lastShownTooltip || lastShownTooltip === EPromoTooltip.PublicPassword) &&
        !isPhone &&
        !isPaidUser &&
        (withPromoTooltip === EPromoTooltip.PublicPassword || (!tooltipPasswordShown && !isCorpUser && !showDownloadLimitsTip));
    const showPublicPasswordTip =
        enabledPublicDownloadLimits && !isPaidUser
            ? defaultShowPublicPasswordTip && tooltipDownloadLimits > MAX_SHOWN_COUNT
            : defaultShowPublicPasswordTip;

    const tooltipDesignShown = Boolean(useSelector((state) => UserStorageSelectors.get(state, PUBLIC_DESIGN_TOOLTIP_ID)));
    const defaultShowPublicDesignTip =
        showChangeDesign &&
        (!lastShownTooltip || lastShownTooltip === EPromoTooltip.ChangeDesign) &&
        (withPromoTooltip === EPromoTooltip.ChangeDesign || (!tooltipDesignShown && !showPublicPasswordTip && !showDownloadLimitsTip));
    const showPublicDesignTip =
        enabledPublicDownloadLimits && !isPaidUser
            ? defaultShowPublicDesignTip && tooltipDownloadLimits > MAX_SHOWN_COUNT
            : defaultShowPublicDesignTip;

    const tooltipDisableDownloadShown = Boolean(
        useSelector((state) => UserStorageSelectors.get(state, DISABLE_PUBLIC_DOWNLOAD_TOOLTIP_ID))
    );
    const defaultShowDisableDownloadTip =
        showDisablePublicDownload &&
        (!lastShownTooltip || lastShownTooltip === EPromoTooltip.DisablePublicDownload) &&
        (withPromoTooltip === EPromoTooltip.DisablePublicDownload ||
            (!tooltipDisableDownloadShown && !showPublicDesignTip && !showPublicPasswordTip && !showDownloadLimitsTip));
    const showDisableDownloadTip =
        enabledPublicDownloadLimits && !isPaidUser
            ? defaultShowDisableDownloadTip && tooltipDownloadLimits > MAX_SHOWN_COUNT
            : defaultShowDisableDownloadTip;

    if (!lastShownTooltip) {
        lastShownTooltip = showDownloadLimitsTip && EPromoTooltip.SharingDownloadLimits;
        lastShownTooltip = lastShownTooltip || (showPublicPasswordTip && EPromoTooltip.PublicPassword);
        lastShownTooltip = lastShownTooltip || (showPublicDesignTip && EPromoTooltip.ChangeDesign);
        lastShownTooltip = lastShownTooltip || (showDisableDownloadTip && EPromoTooltip.DisablePublicDownload);
    }

    const onActionClick = useCallback(() => {
        sendGa('unpublish');
        if (storage === EStorageType.attaches) {
            sendGa('attaches-unpublish');
        }

        if (!item) {
            return;
        }
        unPublishHelper({ items: [item] });
        setWeblinkOpened(false);
    }, [storage, item, setWeblinkOpened]);

    const onChangeWeblink = useCallback(() => {
        if (!item) {
            return;
        }

        sendSharingContentAnalytics({ action: 'switch', label: 'click' });

        if (isWeblinkOpened) {
            renderUnpublishWeblinkDialog({ onActionClick, item, isAlbum });
        } else {
            sendGa('publish');

            dispatch(publishRequest({ item }));
            setWeblinkOpened(true);
        }
    }, [dispatch, setWeblinkOpened, isAlbum, isWeblinkOpened, item, onActionClick, sendSharingContentAnalytics]);

    const onDropdownClick = useCallback(() => {
        sendSharingContentAnalytics({ action: 'auto-delete', label: 'click' });
    }, [sendSharingContentAnalytics]);

    useEffect(() => {
        if (!publicUrl) {
            setWeblinkOpened(false);
        }
    }, [publicUrl, dispatch]);

    useEffect(() => {
        if (item?.weblink) {
            dispatch(getPublicSharingInfo({ public_id: item.weblink }));
            // todo: заменить на новую ручку.
            dispatch(loadPaidInfoRequest());
        }

        if (showChangePassword && !isPaidUser) {
            dispatch(loadPaidInfoRequest());
        }
    }, []);

    if (!item) {
        onCloseModal();
        return null;
    }

    return (
        <SharingNewSection
            onChange={onChangeWeblink}
            isOpened={isWeblinkOpened}
            title={isFolder ? 'По ссылке' : 'Открыть доступ к файлу по ссылке'}
            name="weblink"
            noSwitch={noEdit}
            isPhone={isPhone}
            titleBig={isFolder}
        >
            <>
                <div
                    className={classNames(styles.root, {
                        [styles.root_phone]: isPhone,
                        [styles.root_tutoria]: isWindowSmall,
                        [styles.root_no_edit]: noEdit,
                    })}
                >
                    <div className={styles.copyWeblink}>
                        <CopyWeblink
                            type="new"
                            publicUrl={publicUrl}
                            autoCopy={!isPhone}
                            isPhone={isPhone}
                            isFolder={isFolder}
                            item={item}
                            // tempexp_17341-next-line
                            tooltipEnabledByExp={abSharingLinkFakedoor === 'b' && !isThemedSharedModalItem}
                            folderName={folderName}
                            showOtherSharingTooltipPromo={!isPhone}
                        />
                    </div>

                    {(showDomesticSettings || showAccessRights || showExpires || showAutoDeleteSettings) && !onboardingAllUsers && (
                        <div className={styles.headline}>Общий доступ</div>
                    )}
                    {showInformerLimitSharing && <InformerLimitSharing isMobile={isPhone} total={downloadLimits?.total} />}

                    {onboardingPaidUsers ? (
                        <PaidFeatureBlock
                            context={SharingWidnowContext.SHARRING}
                            from={from}
                            id={id}
                            isPhone={isPhone}
                            itemStorage={itemStorage}
                        />
                    ) : null}

                    {/* tempexp_17605-next-line */}
                    {showChangePassword && (
                        <PublicPassword
                            forcePromo={lastShownTooltip === EPromoTooltip.PublicPassword && Boolean(showPublicPasswordTip)}
                            item={item}
                        />
                    )}
                    {/* tempexp_17341-next-line */}
                    {showChangeDesign && (
                        <ChangeDesign item={item} forcePromo={lastShownTooltip === EPromoTooltip.ChangeDesign && showPublicDesignTip} />
                    )}
                    <DisablePublicDownload
                        show={showDisablePublicDownload}
                        item={item}
                        forcePromo={lastShownTooltip === EPromoTooltip.DisablePublicDownload && showDisableDownloadTip}
                        disabled={!isPaidUser}
                        disableDownload={disableDownload}
                    />
                    {seoOption}
                    {showDomesticSettings && (
                        <SharingOptionDomain
                            item={item}
                            storage={storage}
                            canShareForDomestic={canShareForDomestic}
                            showSharingTooltipPromo={showSharingTooltipPromo}
                            downloadLimits={downloadLimits}
                            formProps={formProps}
                            onCloseModal={onCloseModal}
                        />
                    )}
                    {showAccessRights && item && (
                        <SharingOptionAccessRights
                            item={item}
                            isReadOnly={isReadOnly}
                            isReadOnlyProperty={isReadOnlyProperty}
                            onUnpublish={onUnpublish}
                            showSharingTooltipPromo={showSharingTooltipPromo}
                            downloadLimits={downloadLimits}
                        />
                    )}
                    {showExpires && <SharingOptionExpires item={item} />}
                    {showAutoDeleteSettings && (
                        <AutoDelete
                            item={item}
                            disabled={!isAutodeleteFeatureEnabled && isAutodeletePromoFeatureEnabled && isWeblinkOpened}
                            gaSuffix={gaSuffix}
                            onDropdownClick={onDropdownClick}
                            disabledBySeo={item?.weblinkSeoEnabled && seoEnable}
                        />
                    )}
                </div>
                {showWeblinkListFileSharing && !isAlbum && !isVigbo && !noEdit && showListSharing && (
                    <div data-qa-id="public-personal-access">
                        <div className={styles.headline} data-qa-id="public-personal-access-header">
                            Персональный доступ
                        </div>
                        <SharingNewWeblinkList item={item} from={from} isPhone={isPhone} isReadOnly={isReadOnly} />
                    </div>
                )}
            </>
        </SharingNewSection>
    );
});
